import { type TimelineFeatures, useFeatures } from "@expert/features";
import type { Logger } from "@expert/logging";
import { trackFullStoryEvent } from "@expert/monitoring";
import type { GaiaPayloadConfigs } from "@expert/shared-types";
import { Button, Card, Flex, Stack, Text } from "@mantine/core";
import { useReactAnalytics } from "@soluto-private/eventualize-react";
import {
    type ExpertAssistMessage,
    useSendExpertAssistMessage,
    useTimelineStore,
} from "@soluto-private/expert-workspace-timeline";
import { IconClipboardList } from "@tabler/icons-react";
import { isFromBot } from "node_modules/@soluto-private/expert-workspace-timeline/src/utils";
import { ElapsedTimeValue } from "@/components/chat/CustomerSections/ElapsedTimeValue";
import { type ChatTask, useActiveChatMessages } from "@/sdk";
import { useStopwatch } from "@/shared-utils";

function WaitTime({ startTime }: { startTime: number }) {
    const formattedChatTime = useStopwatch(startTime, true);
    const [timeValue, timeUnit] = formattedChatTime.split(" ");

    return <ElapsedTimeValue value={timeValue} unit={timeUnit} size="sm" />;
}

interface IssueSummaryProps {
    activeTask: ChatTask;
    logger: Logger;
    isExternal: boolean;
    gaiaPayloadProps: GaiaPayloadConfigs;
}

export function IssueSummary({ activeTask, logger, isExternal, gaiaPayloadProps }: IssueSummaryProps) {
    const sendMessage = useSendExpertAssistMessage(logger, isExternal, gaiaPayloadProps);
    const { dispatcher } = useReactAnalytics();
    const {
        features: { helpMeNowQuestion, isHelpMeNowEnabled },
    } = useFeatures<TimelineFeatures>();

    const { currentSessionId, getTimelineState } = useTimelineStore();
    const { chatId, messages: timelineMessages } = getTimelineState(currentSessionId);
    const mostRecentMessage = timelineMessages[timelineMessages.length - 1] as ExpertAssistMessage | undefined;
    const isLoadingBotMessage = !isFromBot(mostRecentMessage?.type ?? "Default");
    const shouldShowSeeRecommendedNextStep = !!chatId && isHelpMeNowEnabled;

    const problemStatement = activeTask.problemStatement;
    const displayText = problemStatement.error ? "Issue unknown" : problemStatement.value;

    const messages = useActiveChatMessages(activeTask);
    const firstCustomerMessage = messages.find((message) => message.senderType === "customer");
    const firstExpertMessage = messages.find((message) => message.senderType === "expert");
    const customerMessageDate = firstCustomerMessage?.dateCreated;

    const onSeeRecommendedNextStepClick = () => {
        logger.info("See recommended next step clicked");
        const type = "Expert";
        const button = "See recommended next step";
        const messageId = crypto.randomUUID();
        void dispatcher.dispatchBusinessEvent("Click_SeeRecommendedNextStep", { button, currentSessionId, messageId });
        trackFullStoryEvent("Click_SeeRecommendedNextStep", {
            button,
            currentSessionId,
            messageId,
        });

        sendMessage({
            text: helpMeNowQuestion,
            type,
            id: messageId,
            metadata: { helpMeNowText: button },
            source: "SeeRecommendedNextStep",
        });
    };

    return (
        <Card radius="md" my="md" ml="xl" mr="0">
            <Card.Section withBorder inheritPadding py="xxs" px="sm" bg="primary.9">
                <Flex justify="space-between" align="center">
                    <Flex align="center" gap={8}>
                        <IconClipboardList size={16} />
                        <Text fz="sm" fw={700}>
                            Issue summary
                        </Text>
                    </Flex>
                    {!firstExpertMessage && (
                        <Flex align="center" gap={4}>
                            <Text fz="sm" c="primary.2">
                                Wait time
                            </Text>
                            {customerMessageDate && <WaitTime startTime={customerMessageDate.getTime()} />}
                        </Flex>
                    )}
                </Flex>
            </Card.Section>
            <Card.Section>
                <Flex direction="column" p="sm" bg={"dark.4"} gap="md">
                    <Stack color={"dark.1"} gap={4}>
                        <Text size="sm">{displayText}</Text>
                    </Stack>
                    {shouldShowSeeRecommendedNextStep && (
                        <Flex gap={"xs"}>
                            <Button
                                variant="outline"
                                size="xm"
                                color="dark.1"
                                radius="xl"
                                loading={isLoadingBotMessage}
                                onClick={onSeeRecommendedNextStepClick}
                                data-testid="see-recommended-next-step"
                            >
                                <Text size="xs">See recommended next step</Text>
                            </Button>
                        </Flex>
                    )}
                </Flex>
            </Card.Section>
        </Card>
    );
}
